import {
	InspectionTemplateColumn,
	InspectionValueTypeCode,
} from "@/shared/models/Inspection";
import { formatLength } from "@/shared/utils/length";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import {
	InspectionReading,
	InspectionRowDraft,
	InspectionValue,
} from "./Inspection";

export type CellDraft = {
	NumericValue: string;
	PassFail: boolean | null;
	TextValue: string;
	DateTimeValue: Date | null;
};

export type DefectDraft = {
	SurfaceDefectId: number;
	SurfaceDefectName: string;
	Color: string | null;
	Comments: string | null;
	Path: string | null;
};

export type ReadingDraft = {
	RowId: number | null;
	RowTimestamp: Date | null;
	HeatId: number | null;
	Cells: Record<number, CellDraft>;
	Defects: DefectDraft[];
};

export const EMPTY_CELL: CellDraft = {
	NumericValue: "",
	PassFail: null,
	TextValue: "",
	DateTimeValue: null,
};

export const buildReadingDraft = (
	columns: InspectionTemplateColumn[],
	reading: InspectionReading | null,
): ReadingDraft => {
	const cells: Record<number, CellDraft> = {};

	for (const column of columns) {
		const columnId = column.ColumnId as number;
		const value = reading?.ValueByColumnId[columnId];

		cells[columnId] = {
			NumericValue:
				value?.NumericValue === null || value?.NumericValue === undefined
					? ""
					: String(value.NumericValue),
			PassFail: value?.PassFail ?? null,
			TextValue: value?.TextValue ?? "",
			DateTimeValue: value?.DateTimeValue
				? new Date(value.DateTimeValue)
				: null,
		};
	}

	return {
		RowId: reading?.RowId ?? null,
		RowTimestamp: reading?.RowTimestamp ?? new Date(),
		HeatId: reading?.HeatId ?? null,
		Cells: cells,
		Defects: (reading?.Defects ?? []).map((defect) => ({
			SurfaceDefectId: defect.SurfaceDefectId,
			SurfaceDefectName: defect.SurfaceDefectName ?? "",
			Color: defect.Color,
			Comments: defect.Comments,
			Path: defect.Path,
		})),
	};
};

const isNumericValueType = (code: InspectionValueTypeCode) =>
	code === "N" || code === "L";

export const toInspectionRowDraft = (
	draft: ReadingDraft,
	inspectionId: number,
	columns: InspectionTemplateColumn[],
): InspectionRowDraft => ({
	RowId: draft.RowId,
	InspectionId: inspectionId,
	RowTimestamp: draft.RowTimestamp,
	HeatId: draft.HeatId,
	Values: columns.map((column) => {
		const cell = draft.Cells[column.ColumnId as number] ?? EMPTY_CELL;

		return {
			ColumnId: column.ColumnId as number,
			NumericValue:
				isNumericValueType(column.ValueTypeCode) && cell.NumericValue !== ""
					? Number(cell.NumericValue)
					: null,
			PassFail: column.ValueTypeCode === "G" ? cell.PassFail : null,
			TextValue:
				column.ValueTypeCode === "T" && cell.TextValue !== ""
					? cell.TextValue
					: null,
			DateTimeValue: column.ValueTypeCode === "D" ? cell.DateTimeValue : null,
		};
	}),
	Defects: draft.Defects.map((defect) => ({
		SurfaceDefectId: defect.SurfaceDefectId,
		Comments: defect.Comments,
	})),
});

export const readCellValue = (
	column: InspectionTemplateColumn,
	value: InspectionValue | undefined,
): number | boolean | string | Date | null => {
	if (!value) return null;
	if (isNumericValueType(column.ValueTypeCode)) return value.NumericValue;
	if (column.ValueTypeCode === "G") return value.PassFail;
	if (column.ValueTypeCode === "D") return value.DateTimeValue;
	return value.TextValue;
};

export const formatCellValue = (
	column: InspectionTemplateColumn,
	value: number | boolean | string | Date | null,
): string => {
	if (value === null || value === undefined || value === "") return "";

	if (column.ValueTypeCode === "L") return formatLength(Number(value));

	if (column.ValueTypeCode === "N")
		return Number(value).toLocaleString("en-US", {
			minimumFractionDigits: 3,
			maximumFractionDigits: 3,
		});

	if (column.ValueTypeCode === "G") return value ? "GO" : "NO GO";

	if (column.ValueTypeCode === "D")
		return dxtToLocalServerTime(value as Date, "MM/dd HH:mm");

	return String(value);
};

export const isCellEmpty = (value: InspectionValue | undefined) =>
	!value ||
	(value.NumericValue === null &&
		value.PassFail === null &&
		value.TextValue === null &&
		value.DateTimeValue === null);
